import { useExponea } from '@/hooks/useExponea'
import { useShopify } from '@/hooks/useShopify'
import { Button, Form, Icon, Notification, Text } from '@lounge-fe/ui-kit'
import { signup, SignupValues } from '@lounge-fe/validation/src/schema/signup'
import { captureException } from '@sentry/react'
import { CountryCode } from '@lounge-fe/ui-kit/src/components/InputPhoneNumber/types'
import { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { SignupFormLayout } from '../SignupFormLayout'
import { SignUpFormProps } from './types'
import classNames from 'classnames'
import { twMerge } from 'tailwind-merge'
import {
  useContentBackground,
  contentPositionMap,
} from '../../hooks/useContentLayout'

export const SignUpForm = ({
  buttonText = '',
  onSuccess,
  eventName,
  content,
  isDarkMode = false,
  updateBloomreachDomain = true,
  className,
  contentAlignment = 'center',
  contentBackground = 'transparent',
  contentPosition = 'center',
}: SignUpFormProps) => {
  const { country, shop } = useShopify()
  const { t } = useTranslation()
  const { track, consent, identify, update } = useExponea()
  const [error, setError] = useState<string>()

  const contentAlignmentMap = {
    center: 'items-center text-center',
    left: 'items-start text-left',
    right: 'items-end text-right',
  }

  const contentBackgroundClass = useContentBackground(contentBackground)

  const onSubmit = ({ ...values }: SignupValues) => {
    try {
      if (values.acceptsMarketing) {
        consent('email', values.acceptsMarketing)
        consent('sms', values.acceptsMarketing)
      }

      const event: Record<string, any> = {
        date: new Date(Date.now()).toISOString(),
        email: values.email,
        phone: values.phone,
        agree: values.acceptsMarketing,
      }

      if (typeof gtag === 'function') gtag('event', eventName, { event })

      identify(values.email.toLowerCase().trim())
      track(eventName, event)
      if (updateBloomreachDomain) {
        update({
          domain: shop ?? 'loungeunderwear.myshopify.com',
        })
      }
      onSuccess()
    } catch (err) {
      captureException(err)
      const error = t('account.errors.unexpected')
      setError(error)
    }
  }

  return (
    <SignupFormLayout.FormContainer
      className={twMerge(
        className,
        'h-full transition-[backdrop-filter] duration-300',
        contentBackgroundClass,
        contentPositionMap[contentPosition],
      )}
    >
      <div
        className={classNames('w-full', contentAlignmentMap[contentAlignment])}
      >
        {content?.map((item, index) => {
          switch (item.type) {
            case 'text':
              return (
                <Text
                  key={index}
                  variant={item.text_size}
                  className={item.margin_bottom}
                  as={item.tag}
                  dangerouslySetInnerHTML={{ __html: item.text }}
                />
              )
            case 'html':
              return (
                <div
                  key={index}
                  dangerouslySetInnerHTML={{ __html: item.html }}
                />
              )
          }
        })}
      </div>
      <Form<SignupValues>
        validationSchema={signup}
        className="w-full relative"
        defaultValues={{
          email: '',
          phone: '',
          acceptsMarketing: false,
        }}
        onSubmit={onSubmit}
      >
        <Form.Input
          labelPosition="outside"
          className={classNames(
            'w-full mt-1 mb-6',
            String.raw`[&_.input-group\_\_control]:border-neutral-400`,
          )}
          name="email"
          testId="email-input"
          placeholder={t('account.email_address')}
        />
        <Form.PhoneInput
          className={classNames(
            'w-full mb-5',
            String.raw`[&_.input-group\_\_control]:border-neutral-400`,
          )}
          label={t('account.phone_number')}
          name="phone"
          testId="phone-input"
          defaultCountry={country as CountryCode}
        />
        <Form.Checkbox
          name="acceptsMarketing"
          className="w-full mb-6 justify-start [&>label]:text-sm [&>label]:font-regular [&>label]:leading-sm sm:flex [&_input[type=checkbox]]:before:border-neutral-400"
          label={t('account.sms_email_marketing_opt_in')}
        />
        <Form.Submit
          disabled={false}
          variant="solid"
          color={isDarkMode ? 'secondary' : 'primary'}
          className="w-full mb-6"
          type="submit"
          testId="submit-button"
        >
          {buttonText}
        </Form.Submit>
        <Text variant="body-sm">
          <Trans
            i18nKey="account.privacy_policy_statement"
            components={{ a: <a /> }}
          />
        </Text>
      </Form>
      {error && (
        <Notification severity="critical" title={t('general.error')}>
          {error}
        </Notification>
      )}
    </SignupFormLayout.FormContainer>
  )
}
